import { useState, useEffect } from 'react'
import { articulosApi } from '../api/notasVenta'
import Boton from './Boton'

function parseClipboard(text) {
  return text
    .split(/\r?\n/)
    .map(line => line.split('\t'))
    .filter(cols => cols[0]?.trim())
    .map(cols => ({
      code:     cols[0]?.trim() || '',
      detail:   cols[1]?.trim() || '',
      cantidad: cols[2] ? (parseInt(cols[2].replace(',', '.')) || 1) : 1,
      precio:   cols[3] ? (parseFloat(cols[3].replace(',', '.')) || null) : null,
    }))
}

export default function PasteExcelModal({ clipboardText, precios = {}, onConfirm, onClose }) {
  const [rows, setRows] = useState([])
  const [buscando, setBuscando] = useState(true)

  useEffect(() => {
    const parsed = parseClipboard(clipboardText || '')
    if (parsed.length === 0) { setBuscando(false); return }

    setRows(parsed.map(p => ({ ...p, articulo: null, estado: 'buscando', descuento: 0 })))
    setBuscando(true)

    Promise.allSettled(
      parsed.map(p =>
        articulosApi.buscar(p.code).then(({ data }) => {
          const items = data.data || data
          return items.find(a => a.codigo_alfa.toLowerCase() === p.code.toLowerCase()) || null
        })
      )
    ).then(results => {
      setRows(prev =>
        prev.map((row, i) => {
          const art = results[i].status === 'fulfilled' ? results[i].value : null
          // Precio explícito en el pegado manda; si no vino, se toma el de la
          // lista activa del cliente (igual que al agregar el artículo a mano).
          const precioLista = art ? (precios[art.id] ?? 0) : 0
          return {
            ...row,
            articulo: art,
            estado: art ? 'ok' : 'no_encontrado',
            detail: row.detail || art?.descripcion || '',
            precio: row.precio !== null ? row.precio : precioLista,
            cantidad: row.cantidad || 1,
          }
        })
      )
      setBuscando(false)
    })
  }, [clipboardText])

  const updateRow = (i, key, val) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r))

  const deleteRow = (i) =>
    setRows(prev => prev.filter((_, idx) => idx !== i))

  const handleConfirm = () => {
    const items = rows
      .filter(r => r.articulo)
      .map(r => ({
        articulo_id: r.articulo.id,
        articulo: { id: r.articulo.id, codigo_alfa: r.articulo.codigo_alfa, descripcion: r.articulo.descripcion },
        cantidad: parseInt(r.cantidad) || 1,
        precio_lista: parseFloat(r.precio) || 0,
        descuento: 0,
        observacion: '',
      }))
    onConfirm(items)
    onClose()
  }

  const countOk = rows.filter(r => r.articulo).length
  const countErr = rows.filter(r => r.estado === 'no_encontrado').length

  return (
    <div style={st.overlay}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.header}>
          <h3 style={st.title}>Vista previa — Pegar desde Excel</h3>
          <button style={st.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={st.body}>
          {buscando ? (
            <p style={st.hintTxt}>Buscando artículos en base de datos... ({rows.length} filas)</p>
          ) : rows.length === 0 ? (
            <p style={st.hintTxt}>No se detectaron filas válidas en el portapapeles. Asegurate de copiar desde Excel con el formato: Código | Descripción | Cantidad | Precio</p>
          ) : (
            <>
              <div style={st.summary}>
                <span style={st.badgeOk}>{countOk} encontrado{countOk !== 1 ? 's' : ''}</span>
                {countErr > 0 && <span style={st.badgeErr}>{countErr} no encontrado{countErr !== 1 ? 's' : ''}</span>}
                <span style={st.summaryNote}>Los no encontrados no se agregarán.</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}></th>
                      <th style={st.th}>Código</th>
                      <th style={st.th}>Descripción</th>
                      <th style={st.th}>Cantidad</th>
                      <th style={st.th}>Precio</th>
                      <th style={st.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} style={row.articulo ? st.rowOk : st.rowErr}>
                        <td style={st.td}>
                          {row.articulo
                            ? <span style={st.dotOk}>✓</span>
                            : <span style={st.dotErr}>✗</span>}
                        </td>
                        <td style={st.td}><code>{row.code}</code></td>
                        <td style={st.td}>{row.detail || row.articulo?.descripcion || '—'}</td>
                        <td style={st.td}>
                          <input
                            style={st.inputSm}
                            type="number" min="1" step="1"
                            value={row.cantidad ?? 1}
                            onChange={e => updateRow(i, 'cantidad', e.target.value)}
                            disabled={!row.articulo}
                          />
                        </td>
                        <td style={st.td}>
                          <input
                            style={st.inputSm}
                            type="number" min="0" step="0.01"
                            value={row.precio ?? ''}
                            onChange={e => updateRow(i, 'precio', e.target.value)}
                            disabled={!row.articulo}
                          />
                        </td>
                        <td style={st.td}>
                          <Boton variante="destructive" sm onClick={() => deleteRow(i)}>✕</Boton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {!buscando && countOk > 0 && (
          <div style={st.footer}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={handleConfirm}>
              Agregar {countOk} artículo{countOk !== 1 ? 's' : ''}
            </Boton>
          </div>
        )}
        {!buscando && countOk === 0 && (
          <div style={st.footer}>
            <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
          </div>
        )}
      </div>
    </div>
  )
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '10px', width: '90%', maxWidth: '820px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' },
  title: { margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1e293b' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 },
  body: { padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: '1px solid #e2e8f0' },
  hintTxt: { color: '#64748b', fontSize: '0.875rem' },
  summary: { display: 'flex', gap: '0.625rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' },
  badgeOk: { background: '#dcfce7', color: '#166534', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8125rem', fontWeight: '600' },
  badgeErr: { background: '#fee2e2', color: '#991b1b', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8125rem', fontWeight: '600' },
  summaryNote: { color: '#64748b', fontSize: '0.8125rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.4rem 0.6rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap' },
  rowOk: { borderBottom: '1px solid #f1f5f9' },
  rowErr: { borderBottom: '1px solid #f1f5f9', background: '#fff5f5' },
  td: { padding: '0.4rem 0.6rem', verticalAlign: 'middle' },
  dotOk: { color: '#16a34a', fontWeight: '700' },
  dotErr: { color: '#dc2626', fontWeight: '700' },
  inputSm: { padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8125rem', width: '72px', boxSizing: 'border-box' },
}
